import { useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface CartProduct {
  id: string;
  name: string;
  main_image: string;
  base_price: number;
}

interface ProductPrice {
  price_type: string;
  price: number;
  offer_price: number | null;
  min_quantity: number;
}

export interface CartItem {
  id: string;
  cart_id: string;
  product_id: string;
  quantity: number;
  price_type: string;
  product: CartProduct | null;
  prices: ProductPrice[];
}

export interface UserCart {
  id: string;
  user_id: string;
  updated_at: string;
  items: CartItem[];
  total: number;
  itemCount: number;
}

export function useUserCart(userId: string) {
  const [cart, setCart] = useState<UserCart | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCart = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      // Obtener carrito del usuario
      const { data: cartData, error: cartError } = await supabase
        .from('cart')
        .select('id, user_id, updated_at')
        .eq('user_id', userId)
        .single();

      if (cartError) {
        if (cartError.code === 'PGRST116') {
          // No tiene carrito
          setCart(null);
          return;
        }
        throw cartError;
      }

      // Obtener items del carrito con productos
      const { data: items, error: itemsError } = await supabase
        .from('cart_items')
        .select(`
          id,
          cart_id,
          product_id,
          quantity,
          price_type,
          product:products(
            id,
            name,
            main_image,
            base_price
          )
        `)
        .eq('cart_id', cartData.id);

      if (itemsError) throw itemsError;

      // Obtener precios de todos los productos
      const productIds = items?.map(i => i.product_id) || [];
      let pricesMap: Record<string, ProductPrice[]> = {};

      if (productIds.length > 0) {
        const { data: pricesData, error: pricesError } = await supabase
          .from('product_prices')
          .select('product_id, price_type, price, offer_price, min_quantity')
          .in('product_id', productIds);

        if (!pricesError && pricesData) {
          pricesMap = pricesData.reduce((acc, price) => {
            if (!acc[price.product_id]) {
              acc[price.product_id] = [];
            }
            acc[price.product_id].push({
              price_type: price.price_type,
              price: price.price,
              offer_price: price.offer_price,
              min_quantity: price.min_quantity
            });
            return acc;
          }, {} as Record<string, ProductPrice[]>);
        }
      }

      // Combinar items con precios
      const itemsWithPrices: CartItem[] = (items || []).map(item => ({
        id: item.id,
        cart_id: item.cart_id,
        product_id: item.product_id,
        quantity: item.quantity,
        price_type: item.price_type,
        product: item.product as unknown as CartProduct,
        prices: pricesMap[item.product_id] || []
      }));

      // Calcular total
      const total = itemsWithPrices.reduce((sum, item) => {
        const priceData = item.prices.find(p => p.price_type === item.price_type);
        const price = priceData?.offer_price ?? priceData?.price ?? item.product?.base_price ?? 0;
        return sum + (price * item.quantity);
      }, 0);

      setCart({
        id: cartData.id,
        user_id: cartData.user_id,
        updated_at: cartData.updated_at,
        items: itemsWithPrices,
        total,
        itemCount: itemsWithPrices.reduce((sum, i) => sum + i.quantity, 0)
      });

    } catch (err) {
      console.error('Error fetching user cart:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar carrito');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const clearCart = useCallback(async () => {
    if (!cart?.id) return false;

    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('cart_id', cart.id);

      if (error) throw error;

      // Actualizar estado local
      setCart(prev => prev ? { ...prev, items: [], total: 0, itemCount: 0 } : null);
      return true;

    } catch (err) {
      console.error('Error clearing cart:', err);
      setError(err instanceof Error ? err.message : 'Error al vaciar carrito');
      return false;
    }
  }, [cart?.id]);

  return { cart, loading, error, fetchCart, clearCart };
}
